import {
    ClientAiQuotaFeatureAvailabilityEntity,
    ClientAiQuotaFeatureAvailabilitySummaryEntity,
    ClientAiQuotaMetricKeyEntity,
    ClientAiQuotaMetricSummaryEntity,
    ClientAiQuotaSummaryEntity,
} from '@/entities/client-ai-quota.entity';
import { ClientFeatureControlsEntity } from '@/entities/client-feature-controls.entity';
import { resolveClientFeatureControlDescriptor } from '@/shared/client-feature-control-policy';

export const AI_QUOTA_EXHAUSTED_REASONS = {
    templateGeneration: 'Лимит анализа ИИ-стратегии исчерпан. Пополните баланс токенов.',
    reviewAnalysis: 'Лимит ИИ-анализа исчерпан. Пополните баланс токенов.',
    aiAnswerGeneration: 'Лимит генерации ИИ-ответов исчерпан. Пополните баланс токенов.',
} as const satisfies Record<ClientAiQuotaMetricKeyEntity, string>;

const AI_QUOTA_METRIC_KEYS: readonly ClientAiQuotaMetricKeyEntity[] = [
    'templateGeneration',
    'reviewAnalysis',
    'aiAnswerGeneration',
];

const toObjectOrNull = (value: unknown): Record<string, unknown> | null => {
    if (!value || typeof value !== 'object' || Array.isArray(value)) {
        return null;
    }

    return value as Record<string, unknown>;
};

export const normalizeQuotaNonNegativeNumber = (value: unknown): number =>
    typeof value === 'number' && Number.isFinite(value) && value >= 0 ? value : 0;

const buildMetricSummary = ({
    cap,
    used,
    reserved,
    remaining,
    exhausted,
}: {
    cap: number;
    used: number;
    reserved: number;
    remaining?: number;
    exhausted?: boolean;
}): ClientAiQuotaMetricSummaryEntity => {
    const normalizedUsed = normalizeQuotaNonNegativeNumber(used);
    const normalizedReserved = normalizeQuotaNonNegativeNumber(reserved);
    const normalizedRemaining =
        typeof remaining === 'number' && Number.isFinite(remaining) && remaining >= 0
            ? remaining
            : Math.max(0, cap - normalizedUsed - normalizedReserved);

    return {
        used: normalizedUsed,
        reserved: normalizedReserved,
        remaining: normalizedRemaining,
        exhausted: typeof exhausted === 'boolean' ? exhausted : normalizedRemaining <= 0,
    };
};

export const createDefaultClientAiQuotaSummary = (): ClientAiQuotaSummaryEntity => ({
    cap: 0,
    metrics: {
        templateGeneration: buildMetricSummary({ cap: 0, used: 0, reserved: 0 }),
        reviewAnalysis: buildMetricSummary({ cap: 0, used: 0, reserved: 0 }),
        aiAnswerGeneration: buildMetricSummary({ cap: 0, used: 0, reserved: 0 }),
    },
});

export const normalizeClientAiQuotaSummaryEntity = (value: unknown): ClientAiQuotaSummaryEntity => {
    const source = toObjectOrNull(value);
    if (!source) {
        return createDefaultClientAiQuotaSummary();
    }

    const cap = normalizeQuotaNonNegativeNumber(source.cap);
    const sourceMetrics = toObjectOrNull(source.metrics);
    const metrics = AI_QUOTA_METRIC_KEYS.reduce(
        (acc, metricKey) => {
            const sourceMetric = toObjectOrNull(sourceMetrics?.[metricKey]);
            acc[metricKey] = buildMetricSummary({
                cap,
                used: normalizeQuotaNonNegativeNumber(sourceMetric?.used),
                reserved: normalizeQuotaNonNegativeNumber(sourceMetric?.reserved),
                remaining:
                    typeof sourceMetric?.remaining === 'number'
                        ? normalizeQuotaNonNegativeNumber(sourceMetric.remaining)
                        : undefined,
                exhausted: typeof sourceMetric?.exhausted === 'boolean' ? sourceMetric.exhausted : undefined,
            });
            return acc;
        },
        {} as Record<ClientAiQuotaMetricKeyEntity, ClientAiQuotaMetricSummaryEntity>,
    );

    return {
        cap,
        metrics,
    };
};

const resolveQuotaAvailability = ({
    manualEnabled,
    manualReason,
    metric,
    quotaReason,
}: {
    manualEnabled: boolean;
    manualReason: string;
    metric: ClientAiQuotaMetricSummaryEntity;
    quotaReason: string;
}) => {
    if (!manualEnabled) {
        return {
            enabled: false,
            reason: manualReason,
            disabledBy: 'feature_control' as const,
        };
    }

    if (metric.exhausted) {
        return {
            enabled: false,
            reason: quotaReason,
            disabledBy: 'quota_exhausted' as const,
        };
    }

    return {
        enabled: true,
        reason: null,
        disabledBy: null,
    };
};

type ClientAiQuotaAvailabilityLike = Pick<ClientAiQuotaFeatureAvailabilityEntity, 'enabled' | 'reason'> &
    Partial<Pick<ClientAiQuotaFeatureAvailabilityEntity, 'disabledBy'>>;

export const isClientAiQuotaExhaustedReason = (reason: string | null | undefined): boolean =>
    Object.values(AI_QUOTA_EXHAUSTED_REASONS).includes(
        reason as (typeof AI_QUOTA_EXHAUSTED_REASONS)[keyof typeof AI_QUOTA_EXHAUSTED_REASONS],
    );

export const isClientAiQuotaExhaustedAvailability = (availability: ClientAiQuotaAvailabilityLike): boolean =>
    !availability.enabled &&
    (availability.disabledBy === 'quota_exhausted' || isClientAiQuotaExhaustedReason(availability.reason));

export const resolveClientAiQuotaFeatureAvailability = ({
    featureControls,
    quota,
}: {
    featureControls: ClientFeatureControlsEntity;
    quota: ClientAiQuotaSummaryEntity;
}): ClientAiQuotaFeatureAvailabilitySummaryEntity => ({
    aiTemplates: resolveQuotaAvailability({
        manualEnabled: featureControls.allowAiAnswerGeneration,
        manualReason: resolveClientFeatureControlDescriptor('allowAiAnswerGeneration').message,
        metric: quota.metrics.templateGeneration,
        quotaReason: AI_QUOTA_EXHAUSTED_REASONS.templateGeneration,
    }),
    aiAnalysis: resolveQuotaAvailability({
        manualEnabled: featureControls.allowAiAnalysis,
        manualReason: resolveClientFeatureControlDescriptor('allowAiAnalysis').message,
        metric: quota.metrics.reviewAnalysis,
        quotaReason: AI_QUOTA_EXHAUSTED_REASONS.reviewAnalysis,
    }),
    aiAnswerGeneration: resolveQuotaAvailability({
        manualEnabled: featureControls.allowAiAnswerGeneration,
        manualReason: resolveClientFeatureControlDescriptor('allowAiAnswerGeneration').message,
        metric: quota.metrics.aiAnswerGeneration,
        quotaReason: AI_QUOTA_EXHAUSTED_REASONS.aiAnswerGeneration,
    }),
});
